import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCurrentUser } from "@/hooks/useAuth";
import { useAdminUsers } from "@/hooks/useApi";
import { useCcDevelopers } from "@/hooks/useCodeCompanion";
import {
  useCcAddTeamMember,
  useCcCreateTeam,
  useCcLinkDevelopers,
  useCcMyTeams,
  useCcRevokeDeveloper,
  useCcTeams,
} from "@/hooks/useCodeCompanionAccess";
import { tp } from "@/lib/plural";

function ini(s: string): string {
  return s
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function Avatar({ label }: { label: string }) {
  return (
    <div
      title={label}
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-extrabold"
      style={{ background: "hsl(var(--primary))", color: "hsl(var(--accent))" }}
    >
      {ini(label)}
    </div>
  );
}

export function DevsScreen() {
  // Gestão de acesso (vincular dev, criar time) é do owner. O dev vê só os
  // times a que pertence (read-only) — sem chamar os endpoints admin.
  const role = useCurrentUser()?.role;
  return role === "admin" ? <OwnerDevsView /> : <MemberDevsView />;
}

function MemberDevsView() {
  const { t } = useTranslation();
  const myTeamsQ = useCcMyTeams();
  const teams = myTeamsQ.data?.items ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-brand text-xl font-semibold text-primary">{t("Devs & Teams")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("Access is managed by the project owner. Here are the teams you belong to.")}
        </p>
      </div>
      <div className="max-w-xl rounded-xl border bg-card">
        <div className="border-b p-4 font-brand text-sm font-semibold text-primary">
          {t("My teams")}
        </div>
        <div className="divide-y">
          {teams.map((tm) => (
            <div key={tm.id} className="p-3">
              <div className="text-[13px] font-semibold">{tm.name}</div>
              <div className="mt-1 flex items-center gap-1">
                {tm.members.map((m) => (
                  <Avatar key={m.user_id} label={m.name ?? m.email ?? m.user_id} />
                ))}
                <span className="ml-2 text-[11px] text-muted-foreground">
                  {tp(t, tm.members.length, "1 member", "{{n}} members")}
                </span>
              </div>
            </div>
          ))}
          {!teams.length && (
            <div className="p-6 text-center text-[11px] text-muted-foreground">
              {t("You don't belong to any team yet. The owner links you to the project.")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OwnerDevsView() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const pid = projectId ?? "";

  const devsQ = useCcDevelopers(projectId);
  const teamsQ = useCcTeams();
  const orgQ = useAdminUsers();
  const link = useCcLinkDevelopers(pid);
  const revoke = useCcRevokeDeveloper(pid);
  const createTeam = useCcCreateTeam();
  const addMember = useCcAddTeamMember();

  const [token, setToken] = useState<{ name: string; value: string } | null>(null);
  const [pick, setPick] = useState("");
  const [teamName, setTeamName] = useState("");

  const linked = useMemo(() => devsQ.data?.items ?? [], [devsQ.data]);
  const linkedIds = useMemo(() => new Set(linked.map((d) => d.user_id)), [linked]);
  const orgUsers = orgQ.data?.users ?? [];
  const candidates = orgUsers.filter((u) => !linkedIds.has(u.id));

  function linkDev() {
    if (!pick) return;
    const u = orgUsers.find((x) => x.id === pick);
    link.mutate(
      { user_id: pick },
      {
        onSuccess: (r) => {
          const g = r.granted[0];
          if (g) setToken({ name: u?.name ?? u?.email ?? pick, value: g.token });
          setPick("");
        },
        onError: () => toast.error(t("Could not link the developer")),
      },
    );
  }

  function linkTeam(teamId: number) {
    link.mutate(
      { team_id: teamId },
      {
        onSuccess: (r) =>
          toast.success(t("Linked team · {{n}} grants issued", { n: r.granted.length })),
        onError: () => toast.error(t("Could not link the team")),
      },
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-brand text-xl font-semibold text-primary">{t("Devs & Teams")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("Link developers or a whole team to this project. Every action stays attributable.")}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.6fr_1fr]">
        {/* roster */}
        <div className="rounded-xl border bg-card">
          <div className="flex items-center justify-between border-b p-4">
            <span className="font-brand text-sm font-semibold text-primary">
              {t("Developer roster")}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {t("{{linked}} linked · {{total}} in org", {
                linked: linked.length,
                total: orgUsers.length,
              })}
            </span>
          </div>

          {/* link a developer */}
          <div className="border-b p-3">
            <div className="flex items-center gap-2">
              <Select value={pick} onValueChange={setPick}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder={t("Pick a developer to link…")} />
                </SelectTrigger>
                <SelectContent>
                  {candidates.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} · {u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" disabled={!pick || link.isPending} onClick={linkDev}>
                {t("Link")}
              </Button>
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">
              {t("Not in the list?")}{" "}
              <Link
                to="/admin/team"
                className="font-semibold text-primary underline-offset-2 hover:underline"
              >
                {t("Add them in Team")}
              </Link>
            </div>
          </div>

          <div className="divide-y">
            {linked.map((d) => (
              <div key={d.user_id} className="flex items-center gap-3 p-3">
                <Avatar label={d.name ?? d.email ?? d.user_id} />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold">{d.name ?? "—"}</div>
                  <div className="truncate font-mono text-[10px] text-muted-foreground">
                    {d.email}
                  </div>
                </div>
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-bold"
                  style={{
                    background: `hsl(var(--success) / 0.12)`,
                    color: `hsl(var(--success))`,
                  }}
                >
                  {d.has_active_grant ? t("Access active") : t("Linked")}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    revoke.mutate(d.user_id, {
                      onSuccess: () => toast.success(t("Access revoked")),
                    })
                  }
                >
                  {t("Revoke")}
                </Button>
              </div>
            ))}
            {!linked.length && (
              <div className="p-6 text-center text-[11px] text-muted-foreground">
                {t("No developers linked yet. Pick one above to issue an access token.")}
              </div>
            )}
          </div>
        </div>

        {/* teams */}
        <div className="space-y-3">
          <div className="rounded-xl border bg-card">
            <div className="border-b p-4 font-brand text-sm font-semibold text-primary">
              {t("Teams")}
            </div>
            <div className="flex items-center gap-2 border-b p-3">
              <input
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder={t("New team name…")}
                className="flex-1 rounded-md border bg-background px-2.5 py-1.5 text-sm"
              />
              <Button
                size="sm"
                disabled={!teamName.trim() || createTeam.isPending}
                onClick={() =>
                  createTeam.mutate(teamName.trim(), {
                    onSuccess: () => {
                      toast.success(t("Team created"));
                      setTeamName("");
                    },
                    onError: () => toast.error(t("Could not create the team")),
                  })
                }
              >
                {t("Create")}
              </Button>
            </div>
            <div className="divide-y">
              {(teamsQ.data?.items ?? []).map((tm) => (
                <div key={tm.id} className="p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-semibold">{tm.name}</span>
                    <Button size="sm" variant="secondary" onClick={() => linkTeam(tm.id)}>
                      {t("Link team")}
                    </Button>
                  </div>
                  <div className="mt-2 flex items-center gap-1">
                    {tm.members.map((m) => (
                      <Avatar key={m.user_id} label={m.name ?? m.email ?? m.user_id} />
                    ))}
                    <span className="ml-2 text-[11px] text-muted-foreground">
                      {tp(t, tm.members.length, "1 member", "{{n}} members")}
                    </span>
                    <div className="flex-1" />
                    <Select
                      value=""
                      onValueChange={(uid) =>
                        addMember.mutate(
                          { teamId: tm.id, userId: uid },
                          { onSuccess: () => toast.success(t("Member added")) },
                        )
                      }
                    >
                      <SelectTrigger className="h-7 w-28 text-[11px]">
                        <SelectValue placeholder={t("+ add")} />
                      </SelectTrigger>
                      <SelectContent>
                        {orgUsers
                          .filter((u) => !tm.members.some((m) => m.user_id === u.id))
                          .map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
              {!teamsQ.data?.items?.length && (
                <div className="p-4 text-center text-[11px] text-muted-foreground">
                  {t("No teams yet.")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* token reveal */}
      <Dialog open={token != null} onOpenChange={(o) => !o && setToken(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Access granted · {{name}}", { name: token?.name ?? "" })}</DialogTitle>
            <DialogDescription>
              {t("Send this token to the developer for their MCP setup.")}
            </DialogDescription>
          </DialogHeader>
          <div
            className="flex items-center gap-2 rounded-lg p-3"
            style={{ background: "hsl(var(--primary))" }}
          >
            <span className="flex-1 break-all font-mono text-xs text-white">
              {token?.value}
            </span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                if (token) navigator.clipboard?.writeText(token.value);
                toast.success(t("Copied"));
              }}
            >
              {t("Copy")}
            </Button>
          </div>
          <div
            className="rounded-lg p-3 text-[11px] font-semibold"
            style={{ background: `hsl(var(--critical) / 0.08)`, color: `hsl(var(--critical))` }}
          >
            {t("You won't see this token again. Copy it now — you can revoke and re-issue later.")}
          </div>
          <DialogFooter>
            <Button onClick={() => setToken(null)}>{t("Done")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
